/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback } from 'react';

/**
 * `NoteFile` type.
 */

export type NoteFile = {
  isLeaf: boolean;
  key: string;
  title: string | ReactNode;
  parent_id: string | null;
  favorite?: boolean;
  updatedAt?: string;
  children: NoteFile[] | undefined;
};

/**
 * Get notes file system.
 */

function getNotesFileSystem(client: AxiosInstance): Promise<NoteFile[]> {
  return client.get(resolveAppUrl('appsNotes')).then(({ data }) => data);
}

/**
 * Export `useGetNotesFileSystemInvalidator`.
 */

export function useGetNotesFileSystemInvalidator() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries(['getNotesFileSystem']);
  }, [queryClient]);
}

/**
 * Export `useGetNotesFileSystem`.
 */

export function useGetNotesFileSystem() {
  const client = useRequestProvider();

  return useQuery(['getNotesFileSystem'], {
    queryFn: async () => {
      return await getNotesFileSystem(client);
    }
  });
}
